import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  OrganizationAdminSummary,
  OrganizationPlan,
  updateOrganizationPlan,
} from '../../services/AdminOrganizations';
import { PLAN_LABELS } from './PlanBadge';

const HINTS: Record<OrganizationPlan, string> = {
  trial: 'Trial limits apply (projects, playbooks, contracts, tokens) until the end date.',
  active: 'Full access until the end date, or open-ended when it is empty.',
  paused: 'Members keep their data but cannot use Queri until resumed.',
  cancelled: 'The plan has ended; members cannot use Queri.',
};

const toDateInput = (iso: string | null) => (iso ? iso.slice(0, 10) : '');
const toNumber = (v: string) => (v.trim() === '' ? null : Number(v));

/** Staff: what the organization may do (plan, end date, seats, tokens) and its cost ceiling. */
const PlanEditor: React.FC<{
  org: OrganizationAdminSummary;
  onSaved: () => void;
}> = ({ org, onSaved }) => {
  const [plan, setPlan] = useState<OrganizationPlan>(org.plan);
  const [endsAt, setEndsAt] = useState(toDateInput(org.planEndsAt));
  const [seats, setSeats] = useState(org.seatLimit?.toString() ?? '');
  const [tokens, setTokens] = useState(org.tokenLimit?.toString() ?? '');
  const [ceiling, setCeiling] = useState(org.costCeilingSek?.toString() ?? '');
  const [multiplier, setMultiplier] = useState(String(org.costMultiplier ?? 1));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setPlan(org.plan);
    setEndsAt(toDateInput(org.planEndsAt));
    setSeats(org.seatLimit?.toString() ?? '');
    setTokens(org.tokenLimit?.toString() ?? '');
    setCeiling(org.costCeilingSek?.toString() ?? '');
    setMultiplier(String(org.costMultiplier ?? 1));
  }, [org]);

  const changed =
    plan !== org.plan ||
    endsAt !== toDateInput(org.planEndsAt) ||
    seats !== (org.seatLimit?.toString() ?? '') ||
    tokens !== (org.tokenLimit?.toString() ?? '') ||
    ceiling !== (org.costCeilingSek?.toString() ?? '') ||
    Number(multiplier) !== Number(org.costMultiplier ?? 1);
  const invalid =
    (plan === 'trial' && !endsAt) ||
    (seats !== '' && !(Number(seats) >= 1)) ||
    (tokens !== '' && !(Number(tokens) >= 0)) ||
    (ceiling !== '' && !(Number.isInteger(Number(ceiling)) && Number(ceiling) >= 1)) ||
    !(Number(multiplier) >= 0.1 && Number(multiplier) <= 100);

  const save = async () => {
    setSaving(true);
    try {
      const res = await updateOrganizationPlan(org.id, {
        plan,
        // End of the chosen day, in UTC
        planEndsAt: endsAt ? `${endsAt}T23:59:59.000Z` : null,
        seatLimit: toNumber(seats),
        tokenLimit: toNumber(tokens),
        costCeilingSek: toNumber(ceiling),
        costMultiplier: Number(multiplier),
      });
      if (res.success) {
        toast.success(`Plan of ${org.displayName || org.name} saved`);
        onSaved();
      } else {
        toast.error(res.errors?.[0]?.detail || res.errors?.[0]?.title || 'Could not save the plan');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="p-4">
      <div className="mb-3 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold">Plan</h2>
          <p className="text-sm text-muted-foreground">{HINTS[plan]}</p>
        </div>
        <Button size="sm" disabled={!changed || invalid || saving} onClick={() => void save()}>
          {saving && <Loader2 className="h-4 w-4 animate-spin" />}
          Save
        </Button>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="space-y-1.5">
          <Label htmlFor="plan">Plan</Label>
          <Select value={plan} onValueChange={(v) => setPlan(v as OrganizationPlan)}>
            <SelectTrigger id="plan">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(PLAN_LABELS) as OrganizationPlan[]).map((p) => (
                <SelectItem key={p} value={p}>
                  {PLAN_LABELS[p]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="plan-ends">Ends</Label>
          <Input id="plan-ends" type="date" value={endsAt} onChange={(e) => setEndsAt(e.target.value)} />
          <p className="text-xs text-muted-foreground">
            {plan === 'trial' ? 'Required for a trial' : 'Empty = open-ended'}
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="plan-seats">Seats</Label>
          <Input
            id="plan-seats"
            type="number"
            min={1}
            placeholder="No limit"
            value={seats}
            onChange={(e) => setSeats(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">Hidden Queri accounts don't count</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="plan-tokens">Token limit</Label>
          <Input
            id="plan-tokens"
            type="number"
            min={0}
            placeholder="No limit"
            value={tokens}
            onChange={(e) => setTokens(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">Applies to trials</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="plan-ceiling">Monthly cost ceiling (SEK)</Label>
          <Input
            id="plan-ceiling"
            type="number"
            min={1}
            placeholder="None"
            value={ceiling}
            onChange={(e) => setCeiling(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">Admins see it on their Cost page and get emails at 80% and 100%</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="plan-multiplier">Price multiplier</Label>
          <Input
            id="plan-multiplier"
            type="number"
            min={0.1}
            step={0.1}
            value={multiplier}
            onChange={(e) => setMultiplier(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">Customer cost = model cost × this. Never shown to the customer</p>
        </div>
      </div>
    </Card>
  );
};

export default PlanEditor;
